import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Badge, Button, Card, IconButton, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { connectorsPath, connectorsTargetElementId } from '../../../lib/connectors-route';
import { OUTREACH_CHANNELS } from '../../../lib/home-map/map-layout';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { ALFRED_OUTREACH_ROW, OUTREACH_CHANNELS_WITH_ALFRED, type OutreachRow } from '../../../lib/outreach-alfred';
import { CONNECTOR_SENDS, outreachLogoUrl, outreachRowState } from '../../../lib/home-map/channels';
import { fetchConnectors, connectorLogoUrl, type ConnectorsStatus } from '../../../lib/connectors';
import { useConnectConnector } from '../../../lib/use-connect-connector';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { queryKeys } from '../../../lib/query-client';
import { markChannelWanted, setOutboundEmail, useOutreachPreferences, type OutboundEmailPreference } from '../../../lib/outreach-preferences';
import { sendFeedback } from '../../../lib/feedback';
import { UnlinkIcon } from './icons';
import { OutreachChannelGlyph } from './OutreachChannelGlyph';
import { AlfredOutreachRow, AlfredOutreachRowSkeleton } from './AlfredOutreachRow';
import styles from './ChannelsOutBody.module.css';

const CONNECTORS_PAGE = { page_name: 'settings-connectors' } as const;
/** The connectors whose mailbox outreach email can leave from: Gmail and Outlook, the two the agent's outreach
 *  tool sends through (`GMAIL_CONNECTOR` names the account, and the harness sends from whichever provider holds
 *  it). The same list iOS (`OutreachPreferences.swift`) and Android (`ChannelsOutTab.kt`) offer. */
const MAIL_TOOLKITS = ['gmail', 'outlook'] as const;

/**
 * Outreach (channels OUT) — the ways Symphony reaches people on the user's behalf: the same fixed list the
 * map's outreach column draws (`OUTREACH_CHANNELS`), one row per channel with WHO sends on it. Email,
 * WhatsApp and SMS are live today (Symphony sends them itself); Instagram DM sends through the user's own
 * connected Instagram (`CONNECTOR_SENDS`); the rest are coming.
 *
 *  - **Instagram DM is a connector channel**: connected → "Replies to your Instagram DMs" and the Connected
 *    badge; not connected → the pitch and Connect; connected but refused by Instagram (`needsReconnect`) →
 *    "Reconnect needed" and Reconnect, never a Connected badge over a channel that cannot send. Both run
 *    the connector's ORDINARY connect (`useConnectConnector`, the Tools card's own rail) right here, so the
 *    reader never leaves this tab; Reconnect is that same connect, as it is on the Tools card. It is reply-only
 *    (Meta lets a business account answer a DM, never open one), and the copy says so.
 *
 *  - **Email's sender is a mechanical setting**, not a chat: with a Gmail/Outlook connected the row offers
 *    "Use my own address" (a single account is picked at once; several are listed to pick from), and shows
 *    which address outreach leaves from with a way back to Symphony's; with none connected it sends the
 *    reader to connect one. The choice is the recipient's typed outbound channel plus the account in
 *    `brain/preferences/outreach.json` (`lib/outreach-preferences.ts`), which the agent reads.
 *  - **A coming-soon channel takes a VOTE**: "I want this" files the wish to the feedback channel in the
 *    background and marks the channel as wanted on the account ("Marked as needed") — no chat opens.
 *  - **Alfred takes Phone's place** behind `alfredInSymphonyOutreach` (`lib/outreach-alfred.ts`): the
 *    coming-soon Phone row is gone and the AI receptionist's row (`AlfredOutreachRow`) sits above iMessage,
 *    drawn from the server's Alfred connector entry — and only when there is one, so the tab never offers a
 *    Connect over a receptionist it cannot see. While that read is in flight the slot shimmers
 *    (`AlfredOutreachRowSkeleton`) instead of standing empty and then popping. Off = today's list, byte for byte.
 *
 * Reached from the map's outreach cards (`/settings/connectors/channels-out/<slug>`), so a slug target
 * scrolls to and highlights that row.
 */
export function ChannelsOutBody({ target, query }: { target?: string; query: string }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const agentName = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const q = query.trim().toLowerCase();
  const { prefs } = useOutreachPreferences();
  const connectors = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 });
  const alfredInOutreach = useAlfredInOutreachEnabled();
  const alfred = connectors.data?.connectors.find((c) => c.toolkit === ALFRED_OUTREACH_ROW.slug);
  const channels: readonly OutreachRow[] = alfredInOutreach ? OUTREACH_CHANNELS_WITH_ALFRED : OUTREACH_CHANNELS;
  const rows = channels.filter((ch) => !q || ch.label.toLowerCase().includes(q));
  const mailAccounts: OutboundEmailPreference[] = (connectors.data?.connectors ?? [])
    .filter((c) => c.connected && (MAIL_TOOLKITS as readonly string[]).includes(c.toolkit))
    .flatMap((c) => (c.accounts ?? []).map((a) => ({ toolkit: c.toolkit, connectedAccountId: a.id, label: a.label || c.name || c.toolkit })));
  const [picking, setPicking] = useState(false);
  const queryClient = useQueryClient();
  const openSideChat = useOpenSideChat();
  const { busy, connect, disconnect } = useConnectConnector({
    getConnectors: () => queryClient.getQueryData<ConnectorsStatus>(queryKeys.connectors)?.connectors ?? [],
    refresh: () => queryClient.invalidateQueries({ queryKey: queryKeys.connectors }),
    openChat: openSideChat,
  });
  const [voted, setVoted] = useState<Record<string, boolean>>({});

  const chooseSender = (pref: OutboundEmailPreference | null) => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: pref ? 'outreach_email_use_own' : 'outreach_email_use_symphony' });
    setPicking(false);
    void setOutboundEmail(pref).catch(() => undefined);
  };
  const want = (slug: string, label: string) => {
    // Already requested → the button is a persistent confirmation, so a re-press does nothing (no second
    // feedback send). `voted`/`prefs` are the current render's, so this guard is fresh each click.
    if (voted[slug] || prefs.wantedChannels.includes(slug)) return;
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: `want_channel_${slug}` });
    setVoted((v) => ({ ...v, [slug]: true }));
    void markChannelWanted(slug).catch(() => setVoted((v) => ({ ...v, [slug]: false })));
    void sendFeedback({ feedback: t('connectors.channelsOut.wantSeed', { channel: label }), topic: `Outreach channel wanted: ${label}` }).catch(() => undefined);
  };

  return (
    <div className={styles.list} data-testid="channels-out">
      {rows.map((ch) => {
        if (ch.slug === ALFRED_OUTREACH_ROW.slug) {
          if (alfred) return <AlfredOutreachRow key={ch.slug} connector={alfred} target={target} />;
          // The slot is Alfred's, but whether the account has the connector is not known until the read
          // answers: its shapes shimmer rather than the row arriving a second after its siblings. Answered
          // (or failed) with no entry → no row.
          return connectors.isLoading ? <AlfredOutreachRowSkeleton key={ch.slug} /> : null;
        }
        const viaToolkit = CONNECTOR_SENDS[ch.slug];
        // The server's row for that toolkit — what the connect runs on. No row (the list has not answered,
        // or the server does not offer the toolkit) means no button: a Connect with nothing behind it is worse.
        const viaConnector = viaToolkit ? connectors.data?.connectors.find((c) => c.toolkit === viaToolkit) : undefined;
        const state = outreachRowState(ch.slug, connectors.data?.connectors ?? []);
        const live = state === 'live';
        // A connector channel is real whether or not it is connected yet: no grey plate, no vote.
        const available = state !== 'comingSoon';
        const highlighted = target === ch.slug;
        const brand = outreachLogoUrl(ch.slug);
        const own = ch.slug === 'email' && prefs.outboundChannel === 'GMAIL_CONNECTOR' ? prefs.outboundEmail : null;
        const wanted = prefs.wantedChannels.includes(ch.slug) || voted[ch.slug];
        // The sender line: "{agent} on {channel}" — and for email, the address it leaves from.
        const sender = viaToolkit
          ? t(live ? 'connectors.channelsOut.instagram.connected' : state === 'reconnect' ? 'connectors.reconnectNeeded' : 'connectors.channelsOut.instagram.description')
          : live
            ? own
              ? t('connectors.channelsOut.sendingFrom', { agent: agentName.short, label: own.label })
              : t('home.map.sendsAs', { agent: agentName.short, channel: ch.label })
            : t('connectors.channelsOut.comingSoonText', { channel: ch.label });
        // iMessage has no real Composio logo (the "same system" the rest use returns a placeholder), so its
        // brand mark is bundled with the app — the way Alfred's is — and named from the mount, never a stylesheet.
        const bundled = ch.slug === 'imessage' ? `${basePath}/imessage.png` : null;
        const logo = own ? connectorLogoUrl(own.toolkit) : bundled ?? brand;
        return (
          <Card key={ch.slug} id={connectorsTargetElementId('channels-out', ch.slug)} variant="sunken" size="sm" className={`${styles.row}${highlighted ? ` ${styles.rowTarget}` : ''}`}>
            {/* The plate + text are one cluster 16 apart (Figma 20690-152934), so the connector image keeps the
                frame's 16 gap to the name; the trailing controls sit on the row's own 12 gap. */}
            <span className={styles.left}>
            <span className={styles.glyph} aria-hidden>
              {logo ? <img src={logo} alt="" width={16} height={16} className={styles.logo} /> : <OutreachChannelGlyph slug={ch.slug} size={16} />}
            </span>
            <span className={styles.main}>
              <Typography variant="h4" component="span" className={styles.name}>
                {ch.label}
              </Typography>
              <Typography variant="p2" color="secondary" component="span" noWrap>
                {sender}
              </Typography>
              {ch.slug === 'email' && picking && (
                <span className={styles.picker} role="group" aria-label={t('connectors.channelsOut.pickAddress')}>
                  {mailAccounts.map((acc) => (
                    <Button key={acc.connectedAccountId} size="tiny" variant="outlined" onClick={() => chooseSender(acc)}>
                      {acc.label}
                    </Button>
                  ))}
                  <Button size="tiny" variant="text" onClick={() => setPicking(false)}>
                    {t('common.cancel')}
                  </Button>
                </span>
              )}
            </span>
            </span>
            {/* A connected connector channel carries the Tools card's own disconnect — an Unlink icon button
                (Figma 20690-152932), wired to the same `disconnect` rail. Symphony-managed channels
                (email/whatsapp/sms) have no connector to unlink, so they show only the Connected badge. */}
            {live && viaConnector && (
              <IconButton
                size="tiny"
                onClick={() => {
                  logPageClickBi({ ...CONNECTORS_PAGE, button_name: `outreach_${ch.slug}_disconnect` });
                  disconnect(viaConnector, ch.label);
                }}
                loading={busy.has(viaConnector.toolkit)}
                title={t('common.disconnect')}
                aria-label={t('common.disconnect')}
              >
                <UnlinkIcon />
              </IconButton>
            )}
            {/* Drop the Connected badge ONLY in email's true "Connect your email" case — no mailbox connected yet
                (`!own && mailAccounts.length === 0`), where a Connected badge beside a Connect action was the
                invalid combo. Email on your OWN Gmail keeps it, and so does email on Symphony's address when a
                mailbox exists to "Replace" (it is live, and Replace is a manage action, valid beside the badge). */}
            {live && !(ch.slug === 'email' && !own && mailAccounts.length === 0) ? (
              <Badge variant="connected" size="tiny" label={t('home.map.connected')} />
            ) : (
              !available && <Badge variant="coming-soon" size="tiny" label={t('connectors.state.comingSoon')} />
            )}
            {viaConnector && (state === 'connect' || state === 'reconnect') && (
              <Button
                size="medium"
                variant="outlined"
                loading={busy.has(viaConnector.toolkit)}
                onClick={() => {
                  logPageClickBi({ ...CONNECTORS_PAGE, button_name: `outreach_${ch.slug}_${state}` });
                  connect(viaConnector);
                }}
              >
                {t(state === 'reconnect' ? 'common.reconnect' : 'common.connect')}
              </Button>
            )}
            {ch.slug === 'email' &&
              (own ? (
                <Button size="medium" variant="outlined" onClick={() => chooseSender(null)}>
                  {t('connectors.channelsOut.symphonyAddress')}
                </Button>
              ) : mailAccounts.length === 0 ? (
                // Nothing to pick from: connecting a mailbox is the first step, on the Tools tab. No target, so it
                // opens on the list rather than on Gmail: an Outlook sends as well (MAIL_TOOLKITS). The BI name predates that.
                <Button size="medium" variant="outlined" href={connectorsPath({ tab: 'tools' })} onClick={() => logPageClickBi({ ...CONNECTORS_PAGE, button_name: 'outreach_email_connect_gmail' })}>
                  {t('connectors.channelsOut.connectMailbox')}
                </Button>
              ) : mailAccounts.length === 1 ? (
                <Button size="medium" variant="outlined" onClick={() => chooseSender(mailAccounts[0])}>
                  {t('connectors.channelsOut.replace')}
                </Button>
              ) : (
                <Button size="medium" variant="outlined" onClick={() => setPicking(true)} aria-expanded={picking}>
                  {t('connectors.channelsOut.replace')}
                </Button>
              ))}
            {!available &&
              (wanted ? (
                // Marked: it STAYS in a readable SECONDARY state with a check prefix (the channel page's
                // "Copied" pattern — an outlined button, not a greyed-disabled one). `want` is idempotent,
                // so a second press is a no-op rather than another request.
                <Button size="medium" variant="outlined" startIcon={<Check size={16} aria-hidden />} onClick={() => want(ch.slug, ch.label)}>
                  {t('connectors.channelsOut.wanted')}
                </Button>
              ) : (
                <Button size="medium" variant="outlined" onClick={() => want(ch.slug, ch.label)}>
                  {t('connectors.channelsOut.want')}
                </Button>
              ))}
          </Card>
        );
      })}
    </div>
  );
}
